import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  SignUpForm as SharedSignUpForm,
  type AuthProviderId,
  type SignUpUserDetails,
} from '@monorepo/shared-lib';
import Logo from 'assets/images/generateTAX-logo.svg';
import ImgWithGoogleSignIn from 'assets/images/google-icon.png';
import ImgWithMicrosoftSignIn from 'assets/images/microsoft-icon.png';
import ImgWithQboSignIn from 'assets/images/quickbook-icon.png';
import SignInWithQbo from 'assets/images/Sign_in_transparent_btn_med_default.svg';
import ImgWithXeroSignIn from 'assets/images/xero_icon.png';
import ImgWithZohoSignIn from 'assets/images/zoho_icon.png';
import routes from 'routes';
import { Provider } from 'store/api/enums';
import {
  BusinessSize,
  BusinessType,
  Country,
  CountryFlag,
} from 'store/common/models';
import { UserDetails } from 'store/signup/models';
import { isZohoEnabled } from 'utils/feature-flag-utils';
import { navigateToExternalPage } from 'utils/validation/common';
import { SSO_SIGNUP_PROVIDER_KEY } from 'utils/sso-signup-provider';
import { reapplyThemeRespectingA11y } from 'utils/theme-preference';

interface SignUpFormProps {
  onRegister: (user: UserDetails, gCaptchaResponse: string) => void;
  countries: Country[];
  countriesExternalData: CountryFlag[];
  businessTypes: BusinessType[];
  businessSizes: BusinessSize[];
  scopeCountries: Country[];
  loading: boolean;
  callProviderSSO: (provider: Provider) => void;
  form: any;
  ssoSignUp: boolean;
}

const providerMap: Record<AuthProviderId, Provider> = {
  xero: Provider.XERO,
  google: Provider.GOOGLE,
  azure: Provider.AZURE,
  quickbooks: Provider.QUICKBOOKS,
  zoho: Provider.ZOHO,
};

export const SignUpForm: React.FC<SignUpFormProps> = ({
  onRegister,
  countries,
  countriesExternalData,
  businessTypes,
  businessSizes,
  scopeCountries: _scopeCountries,
  loading,
  callProviderSSO,
  form,
  ssoSignUp,
}) => {
  const { t } = useTranslation();

  const handleProviderLogin = (provider: AuthProviderId) => {
    if (provider === 'zoho') {
      localStorage.setItem(SSO_SIGNUP_PROVIDER_KEY, Provider.ZOHO);
      const hostURL = `${process.env.PUBLIC_URL}/main`;
      const baseUrl = (process.env.API_URL || '')
        .replace('tax/api', 'tax')
        .replace('8069/api', '8069');
      const providerUrl = `${baseUrl}/oauth2/authorization/zoho?hostURL=${hostURL}`;
      navigateToExternalPage(providerUrl);
      return;
    }
    callProviderSSO(providerMap[provider]);
  };

  return (
    <SharedSignUpForm
      onRegister={(user: SignUpUserDetails, gCaptchaResponse: string) =>
        onRegister(user as UserDetails, gCaptchaResponse)
      }
      onProviderLogin={handleProviderLogin}
      countries={countries}
      countriesExternalData={countriesExternalData}
      businessTypes={businessTypes}
      businessSizes={businessSizes}
      loading={loading}
      form={form}
      ssoSignUp={ssoSignUp}
      labels={{
        tryTitle: t('signup.signup-title-try'),
        siteTitle: t('common.site-title'),
        forWord: t('common.for'),
        freeTag: t('signup.signup-title-free-tag'),
        or: t('signup.OR'),
        description: t('signup.signup-description'),
        firstName: t('signup.first-name'),
        lastName: t('signup.last-name'),
        email: t('signup.email-address'),
        code: t('signup.code'),
        phone: t('contacts.phone'),
        country: t('common.country'),
        companyName: t('signup.company-name'),
        businessType: t('signup.business-type'),
        businessSize: t('signup.business-size'),
        whereToUse: t('signup.where-to-use'),
        otherCountries: t('signup.other-countries'),
        supported: t('signup.supported'),
        notYet: t('signup.not-yet'),
        termsTitle: t('legal.terms-title'),
        privacyTitle: t('legal.privacy-title'),
        iAgreeTo: t('signup.i-agree-to'),
        and: t('common.and'),
        termsWarning: t('signup.terms-warning'),
        signUp: t('common.sign-up'),
        alreadyHaveAccount: t('signup.already-have-account'),
        login: t('login.login'),
        requiredField: t('common.required-field'),
        invalidEmail: t('signup.invalid-email'),
        xeroLogin: t('login.xero-login'),
        googleLogin: t('login.google-login'),
        microsoftLogin: t('login.microsoft-login'),
        qboLogin: t('login.qbo-login'),
        zohoLogin: t('login.zoho-login'),
        logoAriaLabel: t('login.logo-aria-label'),
        emailNotVerified: 'Your email is not verified. Please',
        intuitLoginLink: 'log in to your Intuit account',
        accessibilityMode: t('login.accessibility-mode'),
        accessibilityModeHelp: t('login.accessibility-mode-help'),
      }}
      assets={{
        logo: Logo,
        xero: ImgWithXeroSignIn,
        google: ImgWithGoogleSignIn,
        microsoft: ImgWithMicrosoftSignIn,
        qbo: ImgWithQboSignIn,
        qboHover: SignInWithQbo,
        zoho: ImgWithZohoSignIn,
      }}
      routes={{
        login: routes.login,
        termsOfUse: routes.termsOfUse,
        privacyPolicy: routes.privacyPolicy,
      }}
      captchaSiteKey={process.env.G_CAPTCHA_SITE_KEY || ''}
      showZoho={isZohoEnabled()}
      showA11yToggle={false}
      onA11yDisabled={reapplyThemeRespectingA11y}
    />
  );
};
